/**
 * @fileoverview Complex-mutation operation request/receipt contracts and pure
 *               lifecycle helpers for the complex-inspections contract bundle
 *               (document 16 §6 — the cross-document mutation protocol).
 * @module import-core/contracts/complex-inspections/complexMutationSchemas
 *
 * Responsibilities:
 * - Define the `TenantComplexMutation` request contract: operationId,
 *   operationType, expectedVersions, and per-change operation intents.
 * - Define strict per-operation intent parsing — a `patch` is an operation
 *   intent, NEVER a raw Mongo patch.
 * - Define the `complexMutationReceipts` document contract and the pure
 *   helpers both the server boundary and the local-first client share:
 *   snapshot construction, origin folding, retained-direct origin ids, and
 *   `complexVersionKey`.
 *
 * Dependencies:
 * - `zod` v3; sibling contract modules only (browser-safe — no mongodb/node
 *   runtime imports).
 *
 * Architecture:
 * - Pure schemas + deterministic pure helpers, no I/O. The tenant-api
 *   `complexMutationService` executes these intents inside one Mongo
 *   transaction; the tenant-web local service queues the same request shape
 *   in Dexie before submission.
 *
 * Notes:
 * - ObjectId acceptance is structural (`toHexString()` 24-hex) like
 *   `objectIdLikeSchema`; helpers normalize ids to lowercase hex strings for
 *   comparison and version keys.
 */

import { z } from 'zod';
import {
  objectIdLikeSchema,
  isoDateLikeSchema,
  lookupReferenceSchema,
  contactSnapshotSchema,
  type ContactSnapshotValue,
  type LookupReferenceValue,
} from './sharedSchemas';
import {
  entityContactLinkOriginSchema,
  type EntityContactLinkOrigin,
  type CanonicalContactDocument,
} from './contactSchemas';
import {
  createComplexVisitIntentSchema,
  createComplexReinspectionIntentSchema,
} from './complexVisitPlanning';
// ---------------------------------------------------------------------------
// Request contract (document 16 §6)
// ---------------------------------------------------------------------------

/**
 * @contract complexMutationOperationTypeSchema + ComplexMutationOperationType
 * Purpose: The closed set of cross-document lifecycle operations. Each type
 *   maps to an exact intent shape parsed by `parseComplexMutationIntent`.
 * Fields: the enum literal itself plus the inferred type.
 * Rules: any value outside the enum is rejected at the request boundary.
 * Sync: travels inside `TenantComplexMutation.operationType` on the
 *   complex-mutations route and in the local outbox.
 */
export const complexMutationOperationTypeSchema = z.enum([
  'create-group',
  'transfer-anchor',
  'add-members',
  'change-membership-mode',
  'remove-member',
  'deactivate-group',
  'restore-group',
  'delete-group',
  'add-contact-origin',
  'remove-contact-origin',
  'propagate-contact-edit',
  // Phase 1C — atomic complex visit planning (document 13 §10–§12).
  'create-complex-visit',
  'create-complex-reinspection',
]);
export type ComplexMutationOperationType = z.infer<typeof complexMutationOperationTypeSchema>;

/**
 * @contract complexMutationCollectionSchema + ComplexMutationCollection
 * Purpose: Target collections a complex mutation may write. Phase 1C adds
 *   `cases` — visit planning is the only operation family allowed to write
 *   case documents through this boundary.
 * Fields: the enum literal plus the inferred type.
 * Rules: limited to inspectableEntityGroups, inspectableEntities, contacts,
 *   cases; used to build `expectedVersions`/`completedTargets` keys.
 * Sync: embedded in request `changes[].collection` and version keys.
 */
export const complexMutationCollectionSchema = z.enum([
  'inspectableEntityGroups',
  'inspectableEntities',
  'contacts',
  'cases',
]);
export type ComplexMutationCollection = z.infer<typeof complexMutationCollectionSchema>;

/**
 * @contract complexMutationChangeSchema + ComplexMutationChange
 * Purpose: One declared target of an operation. `patch` is an operation
 *   intent interpreted by the server boundary — it is never a raw Mongo
 *   update document and raw update operators are rejected by intent parsing.
 * Fields: collection, documentId, patch.
 * Rules: `documentId` is a canonical 24-hex id; `patch` is a strict
 *   per-operation intent object validated by `parseComplexMutationIntent`.
 * Sync: part of `TenantComplexMutation.changes` on the wire and in the
 *   local `complex_mutations` outbox row.
 */
export const complexMutationChangeSchema = z
  .object({
    collection: complexMutationCollectionSchema,
    documentId: objectIdLikeSchema,
    patch: z.record(z.string(), z.unknown()),
  })
  .strict();
export type ComplexMutationChange = z.infer<typeof complexMutationChangeSchema>;

/**
 * @contract tenantComplexMutationSchema + TenantComplexMutation
 * Purpose: Complete client-submitted operation request. `operationId` is the
 *   stable client-generated idempotency key; `expectedVersions` keys are
 *   `complexVersionKey(collection, id)` values covering every participant
 *   document the operation touches (not only declared changes).
 * Fields: operationId (UUID), operationType, changes[], expectedVersions.
 * Rules: `expectedVersions` is a strict record of `collection:id` → version;
 *   every required participant must be declared or the server conflicts.
 * Sync: posted to `POST /api/complex-mutations`, replayed from the local
 *   outbox; identical bytes hash to the receipt `requestHash`.
 */
export const tenantComplexMutationSchema = z
  .object({
    operationId: z.string().uuid(),
    operationType: complexMutationOperationTypeSchema,
    expectedVersions: z.record(z.string(), z.number().int().nonnegative()),
    changes: z.array(complexMutationChangeSchema).min(1),
  })
  .strict();
export type TenantComplexMutation = z.infer<typeof tenantComplexMutationSchema>;

// ---------------------------------------------------------------------------
// Receipt contract
// ---------------------------------------------------------------------------

/**
 * @contract complexMutationReceiptSchema + ComplexMutationReceipt
 * Purpose: Durable operation receipt persisted in the tenant collection
 *   `complexMutationReceipts`. `_id` equals `operationId` so a retry with the
 *   same key can never mint a second receipt. `requestHash` is the stable
 *   canonical-JSON hash of the parsed request — a reused operationId with a
 *   different payload returns conflict without mutating the original.
 * Fields: _id/operationId, operationType, requestHash, status
 *   (pending|processing|applied|conflict|failed|unavailable),
 *   completedTargets (`collection:id` of every changed doc), error fields,
 *   processingClaimId (the current processing lease holder), attemptCount,
 *   timestamps.
 * Rules: `_id === operationId` refined; terminal statuses (applied,
 *   conflict, failed) are never rerun — only pending resubmits. Receipt writes
 *   after a claim must match its processingClaimId.
 * Sync: server-authoritative; the client polls GET by operationId to
 *   reconcile the local outbox.
 */
export const complexMutationReceiptSchema = z
  .object({
    _id: z.string().min(1),
    schemaVersion: z.literal(1),
    operationId: z.string().uuid(),
    operationType: complexMutationOperationTypeSchema,
    actorId: objectIdLikeSchema,
    requestHash: z.string().min(1),
    status: z.enum(['pending', 'processing', 'applied', 'conflict', 'failed']),
    affectedCounts: z.object({
      groups: z.number().int().nonnegative(),
      entities: z.number().int().nonnegative(),
      contacts: z.number().int().nonnegative(),
      cases: z.number().int().nonnegative(),
    }),
    completedTargets: z.array(z.string()),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
    processingLeaseUntil: isoDateLikeSchema.optional(),
    processingClaimId: z.string().min(1).optional(),
    createdAt: isoDateLikeSchema,
    updatedAt: isoDateLikeSchema,
    completedAt: isoDateLikeSchema.optional(),
  })
  .strict()
  .superRefine((receipt, ctx) => {
    if (receipt._id !== receipt.operationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['_id'],
        message: 'receipt _id must equal operationId',
      });
    }
  });
export type ComplexMutationReceipt = z.infer<typeof complexMutationReceiptSchema>;

// ---------------------------------------------------------------------------
// Id / version-key helpers
// ---------------------------------------------------------------------------

const OBJECT_ID_HEX = /^[a-f\d]{24}$/i;

/**
 * @process toCanonicalIdString
 * Steps: return lowercase hex for 24-hex strings or ObjectId-like values
 *   exposing toHexString().
 * Inputs: value — string or ObjectId-like.
 * Outputs: lowercase 24-hex string.
 * Failure Handling: throws on values that are not ObjectId-like/24-hex.
 * Idempotency: NOT REQUIRED — pure function.
 */
export function toCanonicalIdString(value: unknown): string {
  if (typeof value === 'string' && OBJECT_ID_HEX.test(value)) return value.toLowerCase();
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toHexString?: unknown }).toHexString === 'function'
  ) {
    const hex = (value as { toHexString: () => string }).toHexString();
    if (OBJECT_ID_HEX.test(hex)) return hex.toLowerCase();
  }
  throw new Error(`toCanonicalIdString: not an ObjectId-like value: ${String(value)}`);
}

/**
 * @process complexVersionKey
 * Steps: compose `${collection}:${toCanonicalIdString(id)}`.
 * Inputs: collection, id.
 * Outputs: deterministic `expectedVersions`/`completedTargets` key.
 * Failure Handling: throws via toCanonicalIdString on malformed ids.
 * Idempotency: NOT REQUIRED — pure function. Both the client request builder
 *   and the server boundary must use this exact key shape.
 */
export function complexVersionKey(collection: ComplexMutationCollection, id: unknown): string {
  return `${collection}:${toCanonicalIdString(id)}`;
}

// ---------------------------------------------------------------------------
// Canonical request hashing material
// ---------------------------------------------------------------------------

function normalizeForCanonicalJson(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(normalizeForCanonicalJson);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (
      typeof record.toHexString === 'function' &&
      OBJECT_ID_HEX.test((record as { toHexString: () => string }).toHexString())
    ) {
      return (record as { toHexString: () => string }).toHexString().toLowerCase();
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      const normalized = normalizeForCanonicalJson(record[key]);
      if (normalized !== undefined) out[key] = normalized;
    }
    return out;
  }
  return value;
}

/**
 * @process canonicalComplexMutationRequestJson
 * Steps: normalizeForCanonicalJson (sort keys, lowercase ids, ISO dates,
 *   drop undefined) then JSON.stringify.
 * Inputs: request — parsed TenantComplexMutation.
 * Outputs: stable canonical JSON string used as the `requestHash` input, so a
 *   retry of the same intent always produces the same digest.
 * Failure Handling: none beyond JSON.stringify.
 * Idempotency: REQUIRED and satisfied — deterministic by construction.
 */
export function canonicalComplexMutationRequestJson(request: TenantComplexMutation): string {
  return JSON.stringify(normalizeForCanonicalJson(request));
}

// ---------------------------------------------------------------------------
// Strict per-operation intent parsing
// ---------------------------------------------------------------------------

const createGroupIntentSchema = z
  .object({
    name: z.string().min(1),
    description: z.string().optional(),
    complexType: lookupReferenceSchema.optional(),
    anchorEntityId: objectIdLikeSchema,
  })
  .strict();

const transferAnchorIntentSchema = z
  .object({
    newAnchorEntityId: objectIdLikeSchema,
    formerAnchorDisposition: z.enum(['complex-governed', 'standalone', 'leave']),
    formerAnchorMemberRole: lookupReferenceSchema.optional(),
  })
  .strict();

const addMembersIntentSchema = z
  .object({
    groupId: objectIdLikeSchema,
    participationMode: z.enum(['complex-governed', 'standalone']),
    memberRole: lookupReferenceSchema.optional(),
    fromGroupId: objectIdLikeSchema.optional(),
  })
  .strict();

const changeMembershipModeIntentSchema = z
  .object({
    groupId: objectIdLikeSchema,
    participationMode: z.enum(['complex-governed', 'standalone']),
  })
  .strict();

const removeMemberIntentSchema = z
  .object({ groupId: objectIdLikeSchema })
  .strict();

const deactivateGroupIntentSchema = z
  .object({ reason: z.string().min(1) })
  .strict();

const restoreGroupIntentSchema = z
  .object({ memberIds: z.array(objectIdLikeSchema).optional() })
  .strict();

const deleteGroupIntentSchema = z
  .object({ reason: z.string().min(1) })
  .strict();

const contactOriginScopeSchema = z.enum(['complex-wide', 'entity-only']);

const addContactOriginIntentSchema = z
  .object({
    scope: contactOriginScopeSchema,
    contactId: objectIdLikeSchema,
    roles: z.array(lookupReferenceSchema).min(1),
    isPrimary: z.boolean(),
    notes: z.string().optional(),
  })
  .strict();

const removeContactOriginIntentSchema = z
  .object({
    scope: contactOriginScopeSchema,
    contactId: objectIdLikeSchema,
    originId: z.string().optional(),
  })
  .strict();

const propagateContactEditIntentSchema = z
  .object({
    contactId: objectIdLikeSchema,
    // Stale-edit guard: the canonical updatedAt the client observed when the
    // edit was persisted locally. The server refuses to fan out snapshots
    // derived from a canonical row older than this timestamp.
    canonicalUpdatedAt: isoDateLikeSchema,
  })
  .strict();

/**
 * @contract ComplexMutationIntent
 * Purpose: The validated per-operation intent — parsed patch payload plus the
 *   change(s) it was declared on. `intent` is the discriminated shape consumed
 *   by the executor; `changes` retains the request declarations.
 * Fields: operationType, changes[], intent.
 * Rules: produced only by `parseComplexMutationIntent`; `intent` matches the
 *   strict per-operation schema (e.g. propagate-contact-edit is exactly
 *   `{ contactId, canonicalUpdatedAt }`).
 * Sync: server-side parse artifact — never persisted or transmitted.
 */
export interface ComplexMutationIntent {
  operationType: ComplexMutationOperationType;
  changes: ComplexMutationChange[];
  intent: Record<string, unknown>;
}

/**
 * @contract ComplexMutationIntentError
 * Purpose: Thrown when a request fails strict per-operation intent
 *   validation; carries the first zod issues for a sanitized 400 response.
 * Fields: message, issues (zod issues).
 * Rules: thrown before any receipt/write — a malformed request is rejected
 *   at the boundary, never queued as a failed operation.
 * Sync: server-side only; surfaces as HTTP 400, not a receipt.
 */
export class ComplexMutationIntentError extends Error {
  readonly issues: z.ZodIssue[];
  constructor(message: string, issues: z.ZodIssue[] = []) {
    super(message);
    this.name = 'ComplexMutationIntentError';
    this.issues = issues;
  }
}

function parseIntentPatch(schema: z.ZodTypeAny, patch: unknown): Record<string, unknown> {
  const result = schema.safeParse(patch);
  if (!result.success) {
    throw new ComplexMutationIntentError(
      `complex mutation intent failed validation: ` +
        result.error.issues
          .slice(0, 5)
          .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
          .join('; '),
      result.error.issues,
    );
  }
  return result.data as Record<string, unknown>;
}

function expectChangeCount(
  request: TenantComplexMutation,
  count: number | 'min1',
  collection: ComplexMutationCollection | 'group-or-entity',
): void {
  const changes = request.changes;
  const countOk = count === 'min1' ? changes.length >= 1 : changes.length === count;
  if (!countOk) {
    throw new ComplexMutationIntentError(
      `operation '${request.operationType}' requires ${count === 'min1' ? 'at least one' : `exactly ${count}`} change(s)`,
    );
  }
  const allowed: ComplexMutationCollection[] =
    collection === 'group-or-entity'
      ? ['inspectableEntityGroups', 'inspectableEntities']
      : [collection];
  changes.forEach((change) => {
    if (!allowed.includes(change.collection)) {
      throw new ComplexMutationIntentError(
        `operation '${request.operationType}' may only target ${allowed.join(' / ')} (got '${change.collection}')`,
      );
    }
  });
}

/**
 * @security parseComplexMutationIntent
 * Purpose: Validate a `TenantComplexMutation` request into its strict
 *   per-operation intent. Rejects extra intent fields, wrong change counts,
 *   wrong target collections, and scope/collection mismatches.
 * Validation: per doc 16 §6 and Phase 1B intent table — see each intent schema.
 * Trust Boundary: called before any domain read/write; a rejected intent
 *   produces no writes.
 *
 * @process parseComplexMutationIntent
 * Steps: dispatch on operationType → assert change count/collection → parse
 *   the strict intent patch schema → return {operationType,changes,intent}.
 * Inputs: request — schema-parsed TenantComplexMutation.
 * Outputs: ComplexMutationIntent.
 * Failure Handling: throws ComplexMutationIntentError (400-class) on any
 *   intent violation; no partial result.
 * Idempotency: NOT REQUIRED — pure validation.
 */
export function parseComplexMutationIntent(request: TenantComplexMutation): ComplexMutationIntent {
  const { operationType, changes } = request;

  switch (operationType) {
    case 'create-group': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(createGroupIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'transfer-anchor': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(transferAnchorIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'add-members': {
      expectChangeCount(request, 'min1', 'inspectableEntities');
      changes.forEach((change) => parseIntentPatch(addMembersIntentSchema, change.patch));
      const groupIds = new Set(changes.map((c) => toCanonicalIdString((c.patch as any).groupId)));
      if (groupIds.size !== 1) {
        throw new ComplexMutationIntentError("operation 'add-members' requires all changes to target the same groupId");
      }
      return {
        operationType,
        changes,
        intent: {
          groupId: changes[0].patch.groupId,
          members: changes.map((change) => ({
            documentId: change.documentId,
            ...parseIntentPatch(addMembersIntentSchema, change.patch),
          })),
        },
      };
    }
    case 'change-membership-mode': {
      expectChangeCount(request, 1, 'inspectableEntities');
      const intent = parseIntentPatch(changeMembershipModeIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'remove-member': {
      expectChangeCount(request, 1, 'inspectableEntities');
      const intent = parseIntentPatch(removeMemberIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'deactivate-group': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(deactivateGroupIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'restore-group': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(restoreGroupIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'delete-group': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(deleteGroupIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'add-contact-origin': {
      expectChangeCount(request, 1, 'group-or-entity');
      const change = changes[0];
      const intent = parseIntentPatch(addContactOriginIntentSchema, change.patch);
      if (change.collection === 'inspectableEntityGroups' && intent.scope !== 'complex-wide') {
        throw new ComplexMutationIntentError("group contact changes require scope 'complex-wide'");
      }
      if (change.collection === 'inspectableEntities' && intent.scope !== 'entity-only') {
        throw new ComplexMutationIntentError("entity contact changes require scope 'entity-only'");
      }
      return { operationType, changes, intent };
    }
    case 'remove-contact-origin': {
      expectChangeCount(request, 1, 'group-or-entity');
      const change = changes[0];
      const intent = parseIntentPatch(removeContactOriginIntentSchema, change.patch);
      if (change.collection === 'inspectableEntityGroups' && intent.scope !== 'complex-wide') {
        throw new ComplexMutationIntentError("group contact changes require scope 'complex-wide'");
      }
      if (change.collection === 'inspectableEntities' && intent.scope !== 'entity-only') {
        throw new ComplexMutationIntentError("entity contact changes require scope 'entity-only'");
      }
      return { operationType, changes, intent };
    }
    case 'propagate-contact-edit': {
      expectChangeCount(request, 1, 'contacts');
      const intent = parseIntentPatch(propagateContactEditIntentSchema, changes[0].patch);
      if (toCanonicalIdString(intent.contactId) !== toCanonicalIdString(changes[0].documentId)) {
        throw new ComplexMutationIntentError(
          "operation 'propagate-contact-edit' requires patch.contactId to equal the change documentId",
        );
      }
      return { operationType, changes, intent };
    }
    case 'create-complex-visit': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(createComplexVisitIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    case 'create-complex-reinspection': {
      expectChangeCount(request, 1, 'inspectableEntityGroups');
      const intent = parseIntentPatch(createComplexReinspectionIntentSchema, changes[0].patch);
      return { operationType, changes, intent };
    }
    default: {
      const exhaustive: never = operationType;
      throw new ComplexMutationIntentError(`unknown operation type '${String(exhaustive)}'`);
    }
  }
}

// ---------------------------------------------------------------------------
// Pure contact snapshot / origin helpers (document 12 §3/§5/§11)
// ---------------------------------------------------------------------------

/**
 * @process buildCanonicalContactSnapshot
 * Steps: pick canonical fields (name/title/company/emails/phones), copy
 *   mailingAddress/address/affiliations when present, stamp
 *   canonicalUpdatedAt, then schema-parse the result.
 * Inputs: contact — canonical contacts document; canonicalUpdatedAt — the
 *   contact's current updatedAt.
 * Outputs: schema-valid ContactSnapshotValue — the ONLY snapshot shape;
 *   callers must never hand-build a reduced snapshot.
 * Failure Handling: throws on schema-invalid contact fields.
 * Idempotency: NOT REQUIRED — pure function; same inputs → same snapshot.
 * Notes: optional scalar fields are OMITTED when null/undefined — the Mongo
 *   driver serializes undefined as BSON null, which contactSnapshotSchema
 *   rejects and which would poison the persisted entity/group document.
 */
export function buildCanonicalContactSnapshot(
  contact: Pick<
    CanonicalContactDocument,
    'displayName' | 'firstName' | 'lastName' | 'title' | 'company' | 'emails' | 'phoneNumbers' | 'updatedAt'
  > & {
    mailingAddress?: CanonicalContactDocument['mailingAddress'];
    address?: string;
    affiliations?: CanonicalContactDocument['affiliations'];
  },
): ContactSnapshotValue {
  const snapshot: ContactSnapshotValue = {
    displayName: contact.displayName,
    emails: contact.emails,
    phoneNumbers: contact.phoneNumbers,
    canonicalUpdatedAt: contact.updatedAt,
  };
  if (contact.firstName != null) snapshot.firstName = contact.firstName;
  if (contact.lastName != null) snapshot.lastName = contact.lastName;
  if (contact.title != null) snapshot.title = contact.title;
  if (contact.company != null) snapshot.company = contact.company;
  if (contact.mailingAddress !== undefined) snapshot.mailingAddress = contact.mailingAddress;
  if (contact.address != null) snapshot.address = contact.address;
  if (contact.affiliations != null) snapshot.affiliations = contact.affiliations;
  return contactSnapshotSchema.parse(snapshot);
}

/**
 * @process foldEntityContactOrigins
 * Steps: union all origin roles de-duplicated by lookupId::code; OR the
 *   isPrimary flags.
 * Inputs: origins — surviving EntityContactLinkOrigin[].
 * Outputs: { effectiveRoles, isPrimary } materialized projections per
 *   doc 12 §5. Direct + complex origins coexist.
 * Failure Handling: none — pure fold.
 * Idempotency: NOT REQUIRED — pure function.
 */
export function foldEntityContactOrigins(origins: EntityContactLinkOrigin[]): {
  effectiveRoles: LookupReferenceValue[];
  isPrimary: boolean;
} {
  const seen = new Set<string>();
  const effectiveRoles: LookupReferenceValue[] = [];
  let isPrimary = false;
  for (const origin of origins) {
    if (origin.isPrimary) isPrimary = true;
    for (const role of origin.roles) {
      const key = `${String(role.lookupId)}::${role.code}`;
      if (!seen.has(key)) {
        seen.add(key);
        effectiveRoles.push(role);
      }
    }
  }
  return { effectiveRoles, isPrimary };
}

/**
 * @process retainedDirectOriginId
 * Steps: compose `retained-direct:${operationId}:${entityId}:${contactId}`.
 * Inputs: operationId, entityId, contactId.
 * Outputs: deterministic originId for a complex origin converted into a
 *   retained direct origin (member removal / move / group delete).
 * Failure Handling: throws via toCanonicalIdString on malformed ids.
 * Idempotency: REQUIRED and satisfied — a retry of the same operation
 *   regenerates the same id and never duplicates origins.
 */
export function retainedDirectOriginId(
  operationId: string,
  entityId: unknown,
  contactId: unknown,
): string {
  return `retained-direct:${operationId}:${toCanonicalIdString(entityId)}:${toCanonicalIdString(contactId)}`;
}

/**
 * @process complexOriginId
 * Steps: compose `complex:${groupId}:${entityId}:${contactId}`.
 * Inputs: contactId, entityId, groupId.
 * Outputs: deterministic originId — uniqueness key (contactId, entityId,
 *   groupId) per doc 12 §5.
 * Failure Handling: throws via toCanonicalIdString on malformed ids.
 * Idempotency: REQUIRED and satisfied — deterministic.
 */
export function complexOriginId(contactId: unknown, entityId: unknown, groupId: unknown): string {
  return `complex:${toCanonicalIdString(groupId)}:${toCanonicalIdString(entityId)}:${toCanonicalIdString(contactId)}`;
}

/**
 * @process directOriginId
 * Steps: compose `direct:${entityId}:${contactId}`.
 * Inputs: entityId, contactId.
 * Outputs: deterministic originId for an entity-only direct origin added
 *   through the operation boundary.
 * Failure Handling: throws via toCanonicalIdString on malformed ids.
 * Idempotency: REQUIRED and satisfied — the same operation retried upserts
 *   rather than duplicates.
 */
export function directOriginId(entityId: unknown, contactId: unknown): string {
  return `direct:${toCanonicalIdString(entityId)}:${toCanonicalIdString(contactId)}`;
}

/**
 * @process convertComplexOriginToRetainedDirect
 * Steps: rebuild the origin as originType 'direct', scope 'entity-only',
 *   preserving roles/isPrimary/linkedAt/linkedBy and assigning the
 *   deterministic retained originId; schema-parse the result.
 * Inputs: origin — the complex origin being retired; retainedId — output of
 *   retainedDirectOriginId.
 * Outputs: schema-valid retained EntityContactLinkOrigin (doc 12 §11).
 * Failure Handling: throws on schema-invalid origin fields.
 * Idempotency: REQUIRED and satisfied — the retained originId is
 *   deterministic so retries upsert, never duplicate.
 */
export function convertComplexOriginToRetainedDirect(
  origin: EntityContactLinkOrigin,
  retainedId: string,
): EntityContactLinkOrigin {
  return entityContactLinkOriginSchema.parse({
    originId: retainedId,
    originType: 'direct',
    scope: 'entity-only',
    roles: origin.roles,
    isPrimary: origin.isPrimary,
    linkedAt: origin.linkedAt,
    linkedBy: origin.linkedBy,
  });
}
